import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { CellWrap } from "@/components/board/cells/CellWrap";
import { Icon } from "@/components/ui/Icon";
import { t } from "@/lib/strings";

interface TextCellProps {
  value: string;
  rowH?: number;
  onPatch?: (value: string) => void;
  sticky?: boolean;
  openable?: boolean;
  onClick?: () => void;
}

export function TextCell({
  value,
  rowH = 44,
  onPatch,
  sticky = false,
  openable = false,
  onClick,
}: TextCellProps) {
  const [hover, setHover] = useState(false);
  const editable = typeof onPatch === "function";
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const baselineRef = useRef(value);
  const revertingRef = useRef(false);

  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  useLayoutEffect(() => {
    if (!editing) return;
    const el = inputRef.current;
    if (!el) return;
    el.focus();
    try {
      el.select();
    } catch {
      /* ignore */
    }
  }, [editing]);

  const enterEdit = useCallback(() => {
    if (!editable || editing) return;
    baselineRef.current = value;
    setDraft(value);
    setEditing(true);
  }, [editable, editing, value]);

  const commit = useCallback(() => {
    if (!onPatch) return;
    if (draft !== baselineRef.current) onPatch(draft);
    baselineRef.current = draft;
  }, [draft, onPatch]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        revertingRef.current = true;
        setDraft(baselineRef.current);
        setEditing(false);
        return;
      }
      if (e.key === "Enter") {
        e.preventDefault();
        commit();
        setEditing(false);
        inputRef.current?.blur();
        return;
      }
      if (e.key === "Tab") {
        commit();
        setEditing(false);
        return;
      }
    },
    [commit],
  );

  const handleBlur = useCallback(() => {
    if (revertingRef.current) {
      revertingRef.current = false;
      return;
    }
    if (editing) {
      commit();
      setEditing(false);
    }
  }, [commit, editing]);

  if (editable && editing) {
    return (
      <CellWrap rowH={rowH} interactive={false}>
        <input
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          spellCheck={false}
          inputMode="text"
          style={{
            width: "100%",
            height: "100%",
            border: "none",
            outline: "none",
            background: "transparent",
            color: "var(--ink-1)",
            fontSize: 13.5,
            fontWeight: sticky ? 500 : 400,
            fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
            padding: 0,
            margin: 0,
          }}
        />
      </CellWrap>
    );
  }

  const wrapClick = editable ? enterEdit : onClick;

  return (
    <CellWrap rowH={rowH} {...(wrapClick ? { onClick: wrapClick } : {})}>
      <div
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{ display: "flex", alignItems: "center", gap: 6, width: "100%", overflow: "hidden" }}
      >
        <span
          title={value}
          style={{
            fontSize: 13.5,
            color: "var(--ink-1)",
            fontWeight: sticky ? 500 : 400,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flex: 1,
            fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
          }}
        >
          {value ? (
            value
          ) : editable && hover ? (
            <span style={{ color: "var(--ink-4)" }}>{t.addTextHint}</span>
          ) : (
            <span style={{ color: "var(--ink-4)" }}>{t.titleEmpty}</span>
          )}
        </span>
        {openable && !editable && hover ? (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              fontSize: 11,
              fontWeight: 500,
              color: "var(--ink-3)",
              background: "var(--surface)",
              border: "1px solid var(--hairline)",
              borderRadius: 5,
              padding: "2px 7px",
              flexShrink: 0,
              fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
            }}
          >
            {t.openChip} <Icon name="arrow" size={10} />
          </span>
        ) : null}
      </div>
    </CellWrap>
  );
}
